import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutGrid, Users, Map, Network, Wrench, CreditCard, Wallet, Ticket, Newspaper, Settings, X, Menu, Bot, TrendingUp, Plus } from 'lucide-react';
import { getStoredStaffRole } from '../utils/staffAuth';

const adminMenuItems = [
  { to: '/command-center', label: 'Command Center', icon: LayoutGrid, roles: ['superadmin', 'admin', 'noc', 'investor', 'manager', 'owner'] },
  { to: '/customers', label: 'Customers', icon: Users, roles: ['superadmin', 'admin'] },
  { to: '/dadu/monitoring', label: 'Topology Live', icon: Network, roles: ['superadmin', 'admin'] },
  { to: '/ai-console', label: 'AI Console', icon: Bot, roles: ['superadmin', 'admin', 'noc'] },
  { to: '/dadu/network-intelligence', label: 'Network Intelligence', icon: TrendingUp, roles: ['superadmin', 'admin', 'noc', 'manager', 'owner', 'investor'] },
  { to: '/dadu/capacity-planning', label: 'Capacity Planning', icon: TrendingUp, roles: ['superadmin', 'admin', 'noc', 'manager', 'owner', 'investor'] },
  { to: '/dadu/ftth', label: 'Topology Plan / Design', icon: Map, roles: ['superadmin', 'admin'] },
  { to: '/dadu/ftth?context=planning&planningAction=olt', label: 'Tambah OLT', icon: Plus, roles: ['superadmin', 'admin'] },
  { to: '/dadu/ftth?context=planning&planningAction=odp', label: 'Tambah ODP', icon: Plus, roles: ['superadmin', 'admin'] },
  { to: '/dadu/billing', label: 'Billing', icon: CreditCard, roles: ['superadmin', 'admin'] },
  { to: '/dadu/keuangan', label: 'Keuangan', icon: Wallet, roles: ['superadmin', 'admin'] },
  { to: '/dadu/penanganan', label: 'Operasional', icon: Wrench, roles: ['superadmin', 'admin'] },
  { to: '/performa-teknisi', label: 'Performa Teknisi', icon: Users, roles: ['superadmin', 'admin', 'technician', 'investor', 'noc'] },
  { to: '/dadu/whatsapp-live', label: 'Live Chat WA', icon: Wrench, roles: ['superadmin', 'admin'] },
  { to: '/dadu/whatsapp-templates', label: 'Template WA', icon: Settings, roles: ['superadmin', 'admin', 'noc'] },
  { to: '/infrastructure', label: 'Infrastructure', icon: Map, roles: ['superadmin', 'admin'] },
  { to: '/dadu/infrastruktur?tab=nas', label: 'NAS / Router', icon: Network, roles: ['superadmin', 'admin'] },
  { to: '/dadu/router-monitoring', label: 'Router Monitoring', icon: Network, roles: ['superadmin', 'admin', 'noc', 'technician', 'investor'] },
  { to: '/dadu/manajemen/ppp-profile', label: 'Paket Internet', icon: Wrench, roles: ['superadmin', 'admin'] },
  { to: '/dadu/manajemen/hotspot-profile', label: 'Profil Hotspot', icon: Wrench, roles: ['superadmin', 'admin'] },
  { to: '/dadu/hotspot/overview', label: 'Hotspot Overview', icon: Ticket, roles: ['superadmin', 'admin'] },
  { to: '/dadu/hotspot/voucher', label: 'Voucher', icon: Ticket, roles: ['superadmin', 'admin'] },
  { to: '/dadu/hotspot/reseller', label: 'Reseller', icon: Users, roles: ['superadmin', 'admin'] },
  { to: '/dadu/berita', label: 'Berita', icon: Newspaper, roles: ['superadmin', 'admin'] },
  { to: '/dadu/kontak', label: 'Pesan Masuk', icon: Users, roles: ['superadmin', 'admin'] },
  { to: '/dadu/pengaturan', label: 'Pengaturan', icon: Settings, roles: ['superadmin', 'admin'] },
  { to: '/dadu/users', label: 'Manajemen User', icon: Users, roles: ['superadmin', 'admin'] },
];

export default function AdminGlobalMenuBar({ desktop = false, onlyWhenFullscreen = false }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const currentRole = getStoredStaffRole();

  const isAdminRoute = useMemo(() => {
    const path = location.pathname || '';
    if (
      path === '/command-center'
      || path === '/topology'
      || path === '/recovery'
      || path === '/customers'
      || path === '/infrastructure'
      || path === '/ai-console'
    ) return false;
    if (path === '/dadu' || path === '/dadu/' || path === '/dadu/dashboard' || path === '/dadu/home' || path === '/dadu/index' || path === '/dadu/pelanggan' || path === '/dadu/infrastruktur') return false;
    return path.startsWith('/command-center')
      || path.startsWith('/topology')
      || path.startsWith('/recovery')
      || path.startsWith('/customers')
      || path.startsWith('/infrastructure')
      || path.startsWith('/ai-console')
      || (path.startsWith('/dadu') && path !== '/dadu/login')
      || path.startsWith('/performa-teknisi');
  }, [location.pathname]);

  useEffect(() => {
    const detectFullscreenLike = () => {
      const apiFullscreen = Boolean(document.fullscreenElement);
      const viewportFullscreenLike =
        typeof window !== 'undefined'
        && typeof window.screen !== 'undefined'
        && window.innerHeight >= (window.screen.height - 6);
      setIsFullscreen(apiFullscreen || viewportFullscreenLike);
    };
    const handleFullscreenChange = () => detectFullscreenLike();
    detectFullscreenLike();
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('resize', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('resize', handleFullscreenChange);
    };
  }, []);

  if (!isAdminRoute) return null;
  if (onlyWhenFullscreen && !isFullscreen) return null;

  const visibleMenuItems = adminMenuItems.filter((item) => {
    if (!Array.isArray(item.roles) || item.roles.length === 0) return true;
    return item.roles.includes(currentRole);
  });

  const isMenuItemActive = (to) => {
    const pureTo = String(to || '').split('?')[0];
    const toQuery = String(to || '').includes('?') ? String(to || '').split('?')[1] : '';
    const targetParams = new URLSearchParams(toQuery);
    const currentParams = new URLSearchParams(String(location.search || ''));
    if (pureTo.startsWith('/dadu/hotspot/')) {
      return location.pathname === pureTo;
    }
    if ([...targetParams.keys()].length > 0) {
      if (location.pathname !== pureTo) return false;
      return [...targetParams.entries()].every(([k, v]) => String(currentParams.get(k) || '') === String(v || ''));
    }
    return location.pathname === pureTo;
  };

  return (
    <div className={`fixed right-4 bottom-4 z-[2600] ${desktop ? '' : 'lg:hidden'}`}>
      {open && (
        <div className="mb-3 w-[min(92vw,420px)] rounded-xl border border-cyan-300/35 bg-[#091120]/92 p-3 shadow-2xl backdrop-blur">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-cyan-100">Menu Admin Cepat</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded p-1 text-cyan-100/80 hover:bg-cyan-500/20"
              aria-label="Tutup menu"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {visibleMenuItems.map((item) => {
              const Icon = item.icon;
              const active = isMenuItemActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={`inline-flex items-center gap-2 rounded-lg border px-2 py-2 text-xs transition ${active ? 'border-cyan-300 bg-cyan-500/20 text-cyan-100' : 'border-cyan-300/20 bg-[#0f1b30]/80 text-slate-200 hover:bg-cyan-500/15'}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-2 rounded-full border border-cyan-300/45 bg-[#0f1b30]/90 px-4 py-2 text-sm font-medium text-cyan-100 shadow-lg hover:bg-cyan-500/15"
      >
        <Menu className="h-4 w-4" />
        Semua Menu
      </button>
    </div>
  );
}

